/**
 * Module Federation utilities - SSOT for remote script loading and container access.
 *
 * Used by:
 * - src/index.ts (preloadAbaxUi) - parallel preload of abax-ui
 * - src/modules/federation/remote-installation.ts - dynamic MFE loading post-signin
 * - dev/abax-ui-remote.js - generic promise remote generator (scope-parametrized)
 *
 * URL allowlist gate (`validateRemoteUrl`) lives in `./validate-url.ts` so
 * `manifest.ts` and other callers can import it without forming a cycle
 * back through index.ts's preload-registry re-exports. Re-exported here for
 * back-compat - callers should switch to importing from `./validate-url`.
 */

export { validateRemoteUrl } from './validate-url';
import { validateRemoteUrl } from './validate-url';
import { getResolvedIntegrity } from './preload-registry';
import { rehomeCdnUrlToDocumentOrigin } from '@abax/platform';
import { getShellBaseUrl } from '@/services/core';

/**
 * Minimum Module Federation container surface needed by this loader.
 * Webpack containers expose both methods; non-webpack containers (Vite shim
 * etc.) may only expose `get` - the consumer code guards `typeof init`.
 */
interface FederationContainer {
  init?: (shareScope: unknown) => Promise<void>;
  get: (module: string) => Promise<() => unknown>;
}

/**
 * Typed window-scope accessor. Webpack assigns the container under
 * `window[scope]` at remote-entry execution time; declaring this as a
 * tightly-typed indexed-access avoids fan-out `as any` casts at every
 * call site (SSOT for the loader's container resolution).
 */
function getFederationContainer(scope: string): FederationContainer | undefined {
  return (window as unknown as Record<string, FederationContainer | undefined>)[scope];
}

// Dedup remote-entry script loads by URL. The previous DOM-presence check
// (`document.scripts.find(s => s.src === url)`) returned `Promise.resolve()`
// the moment the <script> tag appeared in the DOM - that resolves BEFORE the
// browser finishes downloading and executing the bundle. Concurrent callers
// (e.g. preloadAbaxUi + installFederatedPackages racing on the same scope)
// then read `window[scope] === undefined` and crash with
// `TypeError: Cannot read properties of undefined (reading 'init')`.
//
// Per-URL Promise Map: first caller installs the <script>, every later caller
// awaits the same Promise. Resolution flips only on `script.onload` (download
// + execute completed); rejection flips only on `script.onerror`.
const inflightLoads = new Map<string, Promise<void>>();

// Scope-level inflight tracker. `inflightLoads` is keyed by URL only - if two
// concurrent callers pass DIFFERENT URLs for the same scope (e.g.
// `preloadAbaxUi` resolved a manifest URL while `installFederatedPackages`
// processed a backend-federation-config that drifted), both would proceed to
// append separate `<script>` tags racing for `window[scope]`. This second
// map ensures only one install per scope is ever in flight; later callers
// with a drifted URL wait for the first install instead of trampling it.
const inflightInstallsByScope = new Map<string, Promise<void>>();

function loadRemoteEntryScript(url: string, integrity?: string): Promise<void> {
  // No empty-url guard here: installFederatedPackage rejects an empty or
  // malformed url and resolves it to an absolute, validated URL before
  // reaching this helper. This function is not exported; the upstream
  // contract guarantees a non-empty, already-validated absolute URL.
  const existingPromise = inflightLoads.get(url);
  if (existingPromise) return existingPromise;

  const element = document.createElement('script');
  element.src = url;
  element.type = 'text/javascript';
  element.async = true;
  // crossOrigin='anonymous' is required for SRI verification to work AND
  // so future strict-CSP deployments propagate the correct CORS mode. Set
  // unconditionally: deployed environments consume the CDN same-origin (a
  // path on the app's own host), where the attribute changes nothing, and
  // the CDN intentionally serves no Access-Control-Allow-Origin header. The
  // one cross-origin consumer class - a dev document on a loopback origin -
  // has its CDN URLs re-homed onto the dev server's same-origin proxy at
  // config load (dev-cdn-proxy-url.ts).
  element.crossOrigin = 'anonymous';
  // Don't leak the authenticated shell route (Referer) to the CDN edge.
  // Belt-and-suspenders alongside the page-level meta referrer policy.
  element.referrerPolicy = 'no-referrer';
  if (integrity) {
    element.integrity = integrity;
  }

  const promise = new Promise<void>((resolve, reject) => {
    element.onload = () => resolve();
    element.onerror = () => {
      // Pop the failed entry so a retry can attempt a fresh load.
      inflightLoads.delete(url);
      reject(new Error(`[MF] Could not load remote entry: ${url}`));
    };
  });

  inflightLoads.set(url, promise);
  document.head.appendChild(element);
  return promise;
}

export async function installFederatedPackage(scope: string, url: string): Promise<void> {
  // Resolve the entryUrl the same way the browser resolves a <script src>:
  // against the shell's own base (the <base href>). A backend-supplied
  // relative same-origin path (e.g. "../assistant-button/remoteEntry.js" from
  // /federation-config) becomes the shell-origin absolute URL we actually load.
  // Two payoffs: the allowlist sees the EXACT string that gets loaded (no
  // validate-vs-load gap), and a same-origin remote needs no per-environment
  // absolute URL in bloom - it resolves to whatever origin the shell runs on
  // (staging, prod, any tenant domain). Off-origin absolute URLs ignore the
  // base, and protocol-relative URLs only take its protocol, so both still
  // resolve to an absolute URL that validateRemoteUrl rejects as before.
  if (!url) {
    throw new Error(`Refusing to load remote with malformed URL: ${url}`);
  }
  let resolvedUrl: string;
  try {
    // Absolute URL: take as-is (base-independent). A relative URL throws here
    // and is resolved against the shell base below, the same way the browser
    // resolves a <script src>.
    resolvedUrl = new URL(url).href;
  } catch {
    try {
      resolvedUrl = new URL(url, getShellBaseUrl()).href;
    } catch {
      throw new Error(`Refusing to load remote with malformed URL: ${url}`);
    }
  }

  // Re-home a canonical CDN URL onto this document's origin BEFORE validating,
  // so the string that is checked is byte-for-byte the string that is loaded.
  // The script tag below carries crossorigin="anonymous", so a CDN URL left on
  // its canonical origin fails closed wherever the document is served from
  // somewhere else.
  resolvedUrl = rehomeCdnUrlToDocumentOrigin(resolvedUrl);

  // Validate FIRST. The previous order - early-return on `window[scope]`,
  // then validate - let any later caller passing a tainted URL skip the gate
  // once the scope had been loaded once from a clean URL. Keeping the gate
  // ahead of the dedup check closes that bypass. Validate the RESOLVED URL so
  // what we check is exactly what we load.
  if (!validateRemoteUrl(resolvedUrl)) {
    throw new Error(`Refusing to load remote with disallowed origin: ${resolvedUrl}`);
  }

  if (getFederationContainer(scope)) return;

  // Scope-level dedup. If another caller is already installing this scope
  // (typically with the manifest-resolved URL), reuse its promise even if
  // our `url` differs. The first install wins; drifted URLs do not get to
  // append a second <script> for the same scope.
  const existingScopeInstall = inflightInstallsByScope.get(scope);
  if (existingScopeInstall) return existingScopeInstall;

  // Optional SRI hash sourced from manifest fetch. Side-channeled via the
  // preload registry so this signature stays string-only (no breaking
  // change for existing call sites). undefined = no integrity check, just
  // CORS-anonymous load.
  const integrity = getResolvedIntegrity(scope);

  const installPromise = loadRemoteEntryScript(resolvedUrl, integrity);
  inflightInstallsByScope.set(scope, installPromise);
  try {
    await installPromise;
  } finally {
    // Drop the scope entry on settle so a retry after failure can attempt
    // a fresh install with a different URL.
    inflightInstallsByScope.delete(scope);
  }
}

export async function importRemoteModule(scope: string, module: string): Promise<unknown> {
  const container = getFederationContainer(scope);

  // Surface a descriptive error rather than letting `container.init` crash
  // with `Cannot read properties of undefined`. The container should already
  // be present (installFederatedPackage awaits the script onload), so this
  // path is reached only when the bundle loaded but failed to register -
  // typically the stub fallback that sets __ABAX_UI_LOAD_FAILED__.
  if (!container) {
    throw new Error(`[MF] Container not found on window for scope: ${scope}`);
  }

  // Webpack containers have init() for shared scope negotiation.
  // Non-webpack containers (e.g. Vite shim) only expose get().
  if (typeof container.init === 'function') {
    // eslint-disable-next-line no-undef
    await __webpack_init_sharing__('default');
    try {
      // `__webpack_share_scopes__.default` is typed `unknown` by
      // @types/webpack-env; pass it through unchanged to the container.
      // eslint-disable-next-line no-undef
      await container.init(__webpack_share_scopes__.default);
    } catch (error) {
      // Concurrent importRemoteModule calls for the same scope (multiple
      // modules from one MFE) race here: the first init() succeeds and the
      // second throws "already been initialized with a different share scope".
      // The scope is now ready either way, so swallow that case and proceed to
      // get(). Matches the guard in packages/platform ensure-remote-ready.ts.
      //
      // Other failures (corrupt share scope, version mismatch) propagate to
      // container.get() with the container's own diagnostic - log a warning
      // so the init-time signal is visible in console without masking the
      // downstream throw.
      const message = error instanceof Error ? error.message : String(error);
      // Webpack's actual wording is "already been initialized"; the bare
      // "already initialized" pattern never matches it, so match both.
      if (!/already (?:been )?initialized/i.test(message)) {
        // eslint-disable-next-line no-console
        console.warn(`[MF] container.init failed for scope "${scope}":`, error);
      }
    }
  }

  const factory = await container.get(module);
  return factory();
}
